import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

interface ReviewErrorBoundaryProps {
  children: ReactNode;
  resetKey?: string | number | null;
  onReset: () => void;
}

interface ReviewErrorBoundaryState {
  hasError: boolean;
}

export class ReviewErrorBoundary extends Component<ReviewErrorBoundaryProps, ReviewErrorBoundaryState> {
  state: ReviewErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): ReviewErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('Coin Reviewer render failed:', error, info.componentStack);
  }

  componentDidUpdate(prevProps: ReviewErrorBoundaryProps) {
    if (prevProps.resetKey !== this.props.resetKey && this.state.hasError) {
      this.setState({ hasError: false });
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <Card className="border-warning/30 bg-warning/5">
          <CardContent className="py-6 text-center">
            <AlertTriangle className="mx-auto mb-3 h-8 w-8 text-warning" />
            <p className="mb-2 font-medium text-foreground">This review could not render completely.</p>
            <p className="mb-4 text-sm text-muted-foreground">
              Part of the analysis response was incomplete. Please start a new search and try again.
            </p>
            <Button onClick={this.props.onReset} variant="outline">Start New Search</Button>
          </CardContent>
        </Card>
      );
    }

    return this.props.children;
  }
}